import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { KindeProvider, useKindeAuth } from '@kinde-oss/kinde-auth-react';
import jwtDecode from 'jwt-decode';
import { tryit } from 'radash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'src/lib/routing';
import { useClient } from 'urql';

import { deleteCookie, getCookie } from 'lib/cookies';
import { globalVariables } from 'lib/globalVariables';
import { AUTH_CONFIG } from 'network/auth';
import { TRIAL_SESSION_COOKIE } from 'network/authHeaders';
import { isImpersonating } from 'utils/impersonation';

import { initialiseUser } from './initialiseUser';

import type { ReactNode } from 'react';

const isDangerouslyUseLocalStorage = globalVariables.getEnvironmentVariables().kindeIsDangerouslyUseLocalStorage;

/**
 * Wrapper that sets up AUTH_CONFIG.getToken and verifies user is initialized
 * before rendering children. This prevents API calls (like Viewer query) from
 * running before user initialization completes.
 */
const KindeAuthConfigWrapper = ({ children }: { children: ReactNode }) => {
  const { isLoading, isAuthenticated, getToken } = useKindeAuth();
  const [userIsInitialized, setUserIsInitialized] = useState(false);

  // Set up AUTH_CONFIG as soon as authenticated
  // Skip if impersonation token exists - impersonation takes priority over Kinde auth
  useEffect(() => {
    if (isImpersonating()) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    AUTH_CONFIG.getToken = getToken;

    // Authed users must not carry a trial cookie — clear any leftover from the
    // anonymous trial flow so the errorExchange + StreamsTrialGate never see a
    // stale trial session attached to an authed request.
    if (getCookie(TRIAL_SESSION_COOKIE)) {
      deleteCookie(TRIAL_SESSION_COOKIE);
    }
  }, [isAuthenticated, getToken]);

  // Check if user is initialized
  useEffect(() => {
    // Skip if impersonation token exists - impersonation takes priority over Kinde auth
    if (isImpersonating()) {
      setUserIsInitialized(true);
      return;
    }

    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      setUserIsInitialized(false); // Ensure ready is false on logout, preparing for next login
      return;
    }

    // Check if user has been initialized by verifying token properties
    void (async () => {
      const token = await getToken();

      if (!token) {
        setUserIsInitialized(false);
        return;
      }

      const decodedToken = jwtDecode(token) as {
        user_properties: {
          askable_user_id: { t?: string; v?: string };
          askable_user_type: { t?: string; v?: string };
        };
      };

      // If user properties are set, initialization is complete
      if (decodedToken.user_properties.askable_user_id?.v && decodedToken.user_properties.askable_user_type?.v) {
        setUserIsInitialized(true);
      } else {
        setUserIsInitialized(false);
      }
    })();
  }, [isLoading, isAuthenticated, getToken]);

  // Show loading only when authenticated and not ready
  if (isLoading || (!userIsInitialized && isAuthenticated)) {
    return <AskableIconLoading isFullScreen />;
  }

  return <>{children}</>;
};

export function useIsKinde() {
  return !isImpersonating();
}

export const AuthWrapper = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const client = useClient();

  return (
    <KindeProvider
      clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
      domain={import.meta.env.VITE_KINDE_DOMAIN}
      logoutUri={window.location.origin}
      redirectUri={window.location.origin}
      useInsecureForRefreshToken={isDangerouslyUseLocalStorage}
      callbacks={{
        onSuccess: (_user, state, context) => {
          void (async () => {
            const appState = state as { user_type?: string; redirect_uri?: string; utm?: string };

            const [, url] = tryit(() => {
              return new URL(appState.redirect_uri ?? window.location.href);
            })();

            const [, utmObj] = tryit(() => {
              return JSON.parse(appState.utm || '') as { utm_source?: string; utm_campaign?: string };
            })();

            // Only initialize user if user_type is present (new signup flow)
            if (appState.user_type) {
              const [needsReauth, error] = await initialiseUser({
                user_type: appState.user_type,
                utm: utmObj,
                kinde: context,
                client,
              });

              if (error) {
                console.error('User initialization failed, logging out.', error);
                await context.logout();
                return;
              }

              // If user was just initialized, re-login to get fresh token with updated properties
              if (needsReauth) {
                await context.login({
                  state: {
                    redirect_uri: url?.href ?? window.location.href,
                  },
                });
                return;
              }
            }

            navigate(`${url?.pathname}${url?.search}`);
          })();
        },
      }}
    >
      <KindeAuthConfigWrapper>{children}</KindeAuthConfigWrapper>
    </KindeProvider>
  );
};
